import React, { useState, useMemo, useCallback, type ReactNode } from 'react';
import { StudioContext } from './StudioContext';
import { useBrief } from '../hooks/useBrief';
import { useDerivatives } from '../hooks/useDerivatives';
import type { StudioConfig, StudioPanelId, PipelineStep, StudioState, CreativeAngle } from '../types';

const PIPELINE_STEPS: Array<{ id: StudioPanelId; label: string; skill: string }> = [
  { id: 'brief', label: 'Brief', skill: 'get_creative_brief' },
  { id: 'angles', label: 'Hooks & Angles', skill: 'create_creative_angles' },
  { id: 'script', label: 'Script', skill: 'create_ugc_script' },
  { id: 'review', label: 'Review', skill: 'review_creative_asset' },
  { id: 'assets', label: 'Ad Pack', skill: 'create_ad_pack' },
];

interface StudioProviderProps {
  config: StudioConfig;
  children: ReactNode;
}

export function StudioProvider({ config, children }: StudioProviderProps) {
  const [activePanel, setActivePanel] = useState<StudioPanelId>('brief');
  const [loading, setLoading] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);

  const {
    brief,
    briefVersion,
    setBrief,
    updateBrief,
    refreshBrief,
  } = useBrief({
    config,
    initialBrief: config.initial_data?.brief ?? null,
  });

  const apiBase = config.session?.api_base || '';
  const derivatives = useDerivatives(briefVersion, brief);

  const generateAngles = useCallback(async () => {
    if (!brief) return;
    await derivatives.generateAngles(brief, apiBase);
  }, [brief, apiBase, derivatives]);

  const generateScript = useCallback(async (angle: CreativeAngle) => {
    if (!brief) return;
    await derivatives.generateScript(brief, angle, apiBase);
  }, [brief, apiBase, derivatives]);

  const pipelineStatus: PipelineStep[] = useMemo(() => {
    return PIPELINE_STEPS.map((step) => {
      let status: PipelineStep['status'] = 'pending';
      if (step.id === activePanel) status = 'active';
      if (step.id === 'brief' && brief) status = 'completed';
      if (step.id === 'angles' && derivatives.angles.length > 0) status = 'completed';
      if (step.id === 'script' && derivatives.script) status = 'completed';
      if (step.id === 'review' && derivatives.review) status = 'completed';
      if (step.id === activePanel) status = 'active';
      return { ...step, status, skill_name: step.skill };
    });
  }, [activePanel, brief, derivatives.angles, derivatives.script, derivatives.review]);

  const value: StudioState = useMemo(
    () => ({
      config,
      clientUid: config.session?.client_uid || config.client_info?.uid || '',
      brief,
      briefVersion,
      angles: derivatives.angles,
      anglesGenerating: derivatives.anglesGenerating,
      anglesStale: derivatives.anglesStale,
      script: derivatives.script,
      scriptGenerating: derivatives.scriptGenerating,
      scriptStale: derivatives.scriptStale,
      review: derivatives.review,
      reviewStale: derivatives.reviewStale,
      activePanel,
      loading,
      error,
      generationError: derivatives.generationError,
      personas: config.personas || [],
      brandTokens: config.brand_tokens || {},
      pipelineStatus,
      setBrief,
      setAngles: (a) => derivatives.setAngles(a, briefVersion),
      setScript: (s) => derivatives.setScript(s, briefVersion),
      setReview: (r) => derivatives.setReview(r, briefVersion),
      setActivePanel,
      setLoading,
      setError,
      updateBrief,
      refreshBrief,
      generateAngles,
      generateScript,
    }),
    [
      config, brief, briefVersion, derivatives, activePanel,
      loading, error, pipelineStatus, setBrief, updateBrief, refreshBrief,
      generateAngles, generateScript,
    ],
  );

  return (
    <StudioContext.Provider value={value}>
      {children}
    </StudioContext.Provider>
  );
}
